/**
 * DmyDateInput — UAT date entry control.
 *
 * Users type dates in day-month-year order (`28-05-2026`, `28/05/2026`,
 * `28.05.2026`, `28 May 2026`). The component normalises valid values back
 * to ISO date-only strings (`YYYY-MM-DD`) for API payloads and local state.
 */
import { useEffect, useId, useState } from 'react';
import { formatDate, parseDateInput } from '@procurement/shared';
import { Input, type InputProps } from '@/components/ui/form-field';

export interface DmyDateInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'min' | 'max'> {
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** ISO date-only lower bound (`YYYY-MM-DD`). */
  minDate?: string;
  /** ISO date-only upper bound (`YYYY-MM-DD`). */
  maxDate?: string;
  /** Hide the inline validation helper for very dense table/grid layouts. */
  showValidationMessage?: boolean;
}

function displayValue(value: string | null | undefined): string {
  if (!value) return '';
  const formatted = formatDate(value);
  return formatted === '—' ? '' : formatted;
}

function validateDate(
  raw: string,
  minDate?: string,
  maxDate?: string,
): { iso: string; error: null } | { iso: null; error: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { iso: '', error: null };

  const iso = parseDateInput(trimmed);
  if (!iso) {
    return { iso: null, error: 'Enter date as DD-MM-YYYY, for example 28-05-2026.' };
  }
  if (minDate && iso < minDate) {
    return { iso: null, error: `Date cannot be before ${displayValue(minDate)}.` };
  }
  if (maxDate && iso > maxDate) {
    return { iso: null, error: `Date cannot be after ${displayValue(maxDate)}.` };
  }
  return { iso, error: null };
}

export function DmyDateInput({
  value,
  onChange,
  minDate,
  maxDate,
  showValidationMessage = true,
  placeholder = 'DD-MM-YYYY',
  invalid,
  onBlur,
  onFocus,
  ...rest
}: DmyDateInputProps) {
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const describedBy = rest['aria-describedby'];
  const errorId = `${inputId}-error`;
  const [text, setText] = useState(() => displayValue(value));
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setText(displayValue(value));
      setError(null);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally do not reset invalid text on blur

  const effectiveDescribedBy = error && showValidationMessage
    ? [describedBy, errorId].filter(Boolean).join(' ')
    : describedBy;

  return (
    <div>
      <Input
        {...rest}
        id={inputId}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        invalid={invalid || Boolean(error)}
        aria-invalid={invalid || Boolean(error) || undefined}
        aria-describedby={effectiveDescribedBy}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onChange={(event) => {
          const nextText = event.target.value;
          setText(nextText);
          const result = validateDate(nextText, minDate, maxDate);
          setError(result.error);
          if (result.error === null) {
            onChange(result.iso);
          }
        }}
        onBlur={(event) => {
          const result = validateDate(event.target.value, minDate, maxDate);
          setError(result.error);
          if (result.error === null) {
            setText(displayValue(result.iso));
          }
          setFocused(false);
          onBlur?.(event);
        }}
      />
      {error && showValidationMessage ? (
        <p id={errorId} className="mt-1 text-xs text-status-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
